import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { media } from 'context';
import { BLUE_500_HEX, BLUE_50_HEX, GRAY_50_HEX } from 'shared/colors';

import { AnyFilterOption, Column, FilterOption, FilterState, Row } from './types';
import Checkbox from '../Checkbox';
import Circle from '../Circle';
import Clickable from '../Clickable';
import Icon from '../Icon';
import Layout from '../Layout';
import StatusPill from '../StatusPill';
import type { StatusPillPaletteId } from '../StatusPill';
import Text from '../Text';
import { ADMIN_SUPPORT_TICKET_STATUS_TO_COLOR_PROPS, TASK_STATUS_TO_COLOR_PROPS } from '../constants';
import { useOnClickOutside } from '../helpers';
import Input from '../inputs/Input';

interface OneOfOrNotOneOfFilterProps<T extends Row> {
  filterState: FilterState;
  setFilterState: (filterState: FilterState | null) => void;
  column: Column<T>;
}

const OneOfOrNotOneOfFilter = <T extends Row>({
  filterState,
  setFilterState,
  column
}: OneOfOrNotOneOfFilterProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useContext(media);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState<'oneOf' | 'notOneOf'>('oneOf');

  const options = useMemo(
    () => column.filters?.oneOfORNotOneOf?.options ?? [],
    [column.filters?.oneOfORNotOneOf?.options]
  );

  // Helper to get option value (id for FilterOption, value for AnyFilterOption)
  const getOptionValue = (option: FilterOption | AnyFilterOption): string | number | boolean => {
    if ('id' in option && option.id !== undefined) {
      return option.id;
    }
    return option.value;
  };

  // Determine current filter type from filterState
  useEffect(() => {
    if (filterState.notOneOf !== undefined && filterState.notOneOf.length > 0) {
      setFilterType('notOneOf');
    } else if (filterState.oneOf !== undefined && filterState.oneOf.length > 0) {
      setFilterType('oneOf');
    } else {
      setFilterType('oneOf');
    }
  }, [filterState]);

  // Get selected values based on filter type
  const getSelectedValues = (): (string | number | boolean)[] => {
    if (filterType === 'notOneOf') {
      return filterState.notOneOf || [];
    }
    return filterState.oneOf || [];
  };

  const selectedValues = getSelectedValues();

  // Snapshot display order when menu opens; keep it stable while open to avoid jarring reordering on click
  const [displayOrder, setDisplayOrder] = useState<(FilterOption | AnyFilterOption)[]>([]);
  const [firstGroupValues, setFirstGroupValues] = useState<(string | number | boolean)[]>([]);
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen === false) {
      setDisplayOrder([]);
      setFirstGroupValues([]);
      return;
    }

    if (justOpened) {
      const selectedAtOpen = options.filter(option => {
        const optionValue = getOptionValue(option);
        const values = filterType === 'notOneOf' ? filterState.notOneOf : filterState.oneOf;
        return (values || []).includes(optionValue);
      });
      const unselectedAtOpen = options.filter(option => {
        const optionValue = getOptionValue(option);
        const values = filterType === 'notOneOf' ? filterState.notOneOf : filterState.oneOf;
        return !(values || []).includes(optionValue);
      });
      setDisplayOrder([...selectedAtOpen, ...unselectedAtOpen]);
      setFirstGroupValues(selectedAtOpen.map(option => getOptionValue(option)));
    }
  }, [isOpen, options, filterType, filterState.oneOf, filterState.notOneOf]);

  // Use displayOrder when set; fallback to selected-at-top for initial render before effect runs
  const effectiveDisplayOrder =
    displayOrder.length > 0
      ? displayOrder
      : options
          .filter(option => {
            const optionValue = getOptionValue(option);
            return selectedValues.includes(optionValue);
          })
          .concat(options.filter(option => !selectedValues.includes(getOptionValue(option))));

  // Filter options based on search text (applied to stable display order)
  const displayOptions = effectiveDisplayOrder.filter(option => {
    if (!searchText.trim()) return true;
    const searchLower = searchText.toLowerCase();
    return option.label.toLowerCase().includes(searchLower);
  });

  // Support status filter has options like waiting_on_us, waiting_on_contact (task status filter does not)
  const isSupportStatusFilter =
    column.key === 'status' &&
    options.some(opt => ['waiting_on_us', 'waiting_on_contact'].includes(getOptionValue(opt) as string));

  // Check if option is selected
  const isOptionSelected = (option: FilterOption | AnyFilterOption): boolean => {
    const optionValue = getOptionValue(option);
    return selectedValues.includes(optionValue);
  };

  // Toggle option selection
  const toggleOption = (option: FilterOption | AnyFilterOption) => {
    const value = getOptionValue(option);
    const currentSelected = [...selectedValues];

    if (isOptionSelected(option)) {
      // Remove from selection
      const newSelected = currentSelected.filter(v => v !== value);
      if (newSelected.length === 0) {
        setFilterState(null);
      } else {
        setFilterState({
          ...filterState,
          key: filterState.key,
          oneOf: filterType === 'oneOf' ? newSelected : undefined,
          notOneOf: filterType === 'notOneOf' ? newSelected : undefined
        });
      }
    } else {
      // Add to selection
      const newSelected = [...currentSelected, value];
      setFilterState({
        ...filterState,
        key: filterState.key,
        oneOf: filterType === 'oneOf' ? newSelected : undefined,
        notOneOf: filterType === 'notOneOf' ? newSelected : undefined
      });
    }
  };

  // Handle filter type change
  const handleFilterTypeChange = (newType: 'oneOf' | 'notOneOf') => {
    if (newType === filterType) return;

    const currentSelected = getSelectedValues();
    setFilterType(newType);

    if (currentSelected.length === 0) {
      return;
    }

    setFilterState({
      ...filterState,
      key: filterState.key,
      oneOf: newType === 'oneOf' ? currentSelected : undefined,
      notOneOf: newType === 'notOneOf' ? currentSelected : undefined
    });
  };

  // Clear all selections
  const handleClear = () => {
    setFilterState(null);
    setSearchText('');
    setIsOpen(false);
    setIsFilterTypeDropdownOpen(false);
  };

  // Generate display text
  const getDisplayText = (): string => {
    const header = column.header;
    const selectedCount = selectedValues.length;

    if (selectedCount === 0) {
      return `Any ${header}`;
    }

    // Find the first selected option
    const firstSelectedOption = options.find(option => {
      const optionValue = getOptionValue(option);
      return selectedValues.includes(optionValue);
    });

    const firstLabel = firstSelectedOption?.label || '';
    const typeLabel = filterType === 'oneOf' ? '=' : '≠';

    if (selectedCount === 1) {
      return `${header} ${typeLabel} ${firstLabel}`;
    }

    const remainingCount = selectedCount - 1;
    return `${header} ${typeLabel} ${firstLabel} +${remainingCount}`;
  };

  const hasSelections = selectedValues.length > 0;

  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
    setIsFilterTypeDropdownOpen(false);
    setSearchText('');
  });

  const [isFilterTypeDropdownOpen, setIsFilterTypeDropdownOpen] = useState(false);
  const filterTypeDropdownRef = useRef<HTMLDivElement>(null);

  return (
    <Layout __ref={containerRef} position="relative" width={isMobile ? '100%' : 'fit-content'} borderRadius={8}>
      <button
        style={{
          paddingLeft: 10,
          paddingRight: 12,
          height: isMobile ? '48px' : '32px',
          borderRadius: '8px',
          border: isOpen || hasSelections ? `1px solid ${BLUE_500_HEX}` : `1px solid ${GRAY_50_HEX}`,
          width: isMobile ? '100%' : undefined,
          backgroundColor: isOpen || hasSelections ? BLUE_50_HEX : undefined
        }}
        onClick={() => setIsOpen(!isOpen)}
        className="hover-bg-color-gray-25"
      >
        <Layout direction="row" align="center" gap={isMobile ? 8 : 4} justify={isMobile ? 'space-between' : undefined}>
          <Text size="body-sm" wrap={false} color={`${isOpen || hasSelections ? 'blue-500' : 'black'}`}>
            {getDisplayText()}
          </Text>
          {!hasSelections && <Icon name="downCaret" size="xs" />}
        </Layout>
      </button>
      {isOpen && (
        <Layout
          background="white"
          position="absolute"
          color="white"
          paddingTop={12}
          //   paddingX={12}
          paddingBottom={12}
          boxShadow="0px 8px 24px 0px rgba(0, 0, 0, 0.12)"
          borderRadius={8}
          width={isMobile ? '100%' : 240}
          top="100%"
          marginTop={4}
          zIndex={2000}
        >
          <Layout direction="column" gap={12}>
            {/* Filter Type Dropdown */}
            <Layout position="relative" __ref={filterTypeDropdownRef} paddingX={12}>
              <button
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  height: '32px',
                  borderRadius: '8px',
                  border: isFilterTypeDropdownOpen ? `2px solid ${BLUE_500_HEX}` : `1px solid ${GRAY_50_HEX}`,
                  width: '100%',
                  backgroundColor: `${isFilterTypeDropdownOpen ? BLUE_50_HEX : 'white'}`
                }}
                onClick={() => setIsFilterTypeDropdownOpen(!isFilterTypeDropdownOpen)}
                className="hover-bg-color-gray-50"
              >
                <Layout direction="row" align="center" gap={4} justify="space-between">
                  <Text size="body-sm" wrap={false} color={`${isFilterTypeDropdownOpen ? 'blue-500' : 'black'}`}>
                    {column.header} {filterType === 'oneOf' ? '= (Is)' : '≠ (Is not)'}
                  </Text>
                  <Icon name="downCaret" size="xs" />
                </Layout>
              </button>
              {isFilterTypeDropdownOpen && (
                <Layout
                  position="absolute"
                  background="white"
                  paddingY={8}
                  boxShadow="0px 8px 24px 0px rgba(0, 0, 0, 0.12)"
                  borderRadius={8}
                  width="100%"
                  top="100%"
                  marginTop={4}
                  zIndex={2001}
                  hoverColor="gray-50"
                >
                  <button
                    onClick={() => {
                      handleFilterTypeChange('oneOf');
                      setIsFilterTypeDropdownOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      textAlign: 'left',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    className="hover-bg-color-gray-50"
                  >
                    <Text size="body-xs" wrap={false}>
                      {column.header} = (Is)
                    </Text>
                  </button>
                  <button
                    onClick={() => {
                      handleFilterTypeChange('notOneOf');
                      setIsFilterTypeDropdownOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      textAlign: 'left',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    className="hover-bg-color-gray-50"
                  >
                    <Text size="body-xs" wrap={false}>
                      {column.header} ≠ (Is not)
                    </Text>
                  </button>
                </Layout>
              )}
            </Layout>

            {/* Search Input */}
            <Layout paddingX={12}>
              <Input
                value={searchText}
                onChange={({ value }) => setSearchText(value)}
                placeholder="Search options..."
                size="xxs"
                icon="search"
                onClear={() => setSearchText('')}
                label=""
              />
            </Layout>

            {/* Options List */}
            <Layout
              direction="column"
              maxHeight={288}
              overflowY="auto"
              overflowX="hidden"
              paddingY={4}
              style={{
                borderTop: `1px solid ${GRAY_50_HEX}`,
                borderBottom: `1px solid ${GRAY_50_HEX}`
              }}
            >
              {displayOptions.length === 0 ? (
                <Layout paddingY={16} paddingX={12}>
                  <Text size="body-xs" color="gray-600">
                    No options found
                  </Text>
                </Layout>
              ) : (
                (() => {
                  const firstGroupValuesSet = new Set(firstGroupValues);
                  let hasSeenFirstGroupItem = false;
                  let dividerRendered = false;

                  return displayOptions.map(option => {
                    const isSelected = isOptionSelected(option);
                    const optionKey = String(getOptionValue(option));
                    const optionValue = getOptionValue(option);
                    const isInFirstGroup = firstGroupValuesSet.has(optionValue);

                    let showDivider = false;
                    if (!isInFirstGroup && hasSeenFirstGroupItem && !dividerRendered && firstGroupValues.length > 0) {
                      showDivider = true;
                      dividerRendered = true;
                    }

                    if (isInFirstGroup) {
                      hasSeenFirstGroupItem = true;
                    }

                    const getStatusPillProps = column.filters?.oneOfORNotOneOf?.getStatusPillProps;
                    const statusPillProps = getStatusPillProps != null ? getStatusPillProps(optionValue) : undefined;
                    const isTaskStatusFilter =
                      column.key === 'status' &&
                      typeof optionValue === 'string' &&
                      optionValue in TASK_STATUS_TO_COLOR_PROPS &&
                      !isSupportStatusFilter;
                    const isSupportStatusOption =
                      column.key === 'status' &&
                      typeof optionValue === 'string' &&
                      isSupportStatusFilter &&
                      optionValue in ADMIN_SUPPORT_TICKET_STATUS_TO_COLOR_PROPS;
                    const statusColorProps = isTaskStatusFilter
                      ? TASK_STATUS_TO_COLOR_PROPS[optionValue as keyof typeof TASK_STATUS_TO_COLOR_PROPS]
                      : isSupportStatusOption
                        ? ADMIN_SUPPORT_TICKET_STATUS_TO_COLOR_PROPS[
                            optionValue as keyof typeof ADMIN_SUPPORT_TICKET_STATUS_TO_COLOR_PROPS
                          ]
                        : null;
                    const useStatusPill = statusPillProps != null;
                    const isLegacyStatusFilter =
                      (isTaskStatusFilter || isSupportStatusOption) && statusColorProps != null && !useStatusPill;

                    return (
                      <Layout key={optionKey}>
                        {showDivider && (
                          <Layout
                            marginX={12}
                            style={{
                              borderTop: `1px solid ${GRAY_50_HEX}`
                            }}
                          />
                        )}
                        <Layout hoverColor="gray-50">
                          <Layout paddingY={8} paddingX={12} position="relative" direction="row" align="center" gap={8}>
                            {useStatusPill ? (
                              <Clickable
                                onClick={e => {
                                  e.preventDefault();
                                  toggleOption(option);
                                }}
                              >
                                <Layout direction="row" align="center" gap={8}>
                                  <Checkbox
                                    value={isSelected ? 'checked' : 'off'}
                                    onChange={() => toggleOption(option)}
                                    label=""
                                    size="sm"
                                    wrap={true}
                                  />
                                  <StatusPill
                                    palette={statusPillProps.palette as StatusPillPaletteId}
                                    label={statusPillProps.label}
                                    size="sm"
                                  />
                                </Layout>
                              </Clickable>
                            ) : (
                              <>
                                <Checkbox
                                  value={isSelected ? 'checked' : 'off'}
                                  onChange={() => toggleOption(option)}
                                  label={isLegacyStatusFilter ? '' : option.label}
                                  size="sm"
                                  wrap={true}
                                />
                                {isLegacyStatusFilter && statusColorProps && (
                                  <Clickable
                                    onClick={e => {
                                      e.preventDefault();
                                      toggleOption(option);
                                    }}
                                  >
                                    <Layout
                                      color={statusColorProps.backgroundColor}
                                      height={20}
                                      borderRadius={1000}
                                      paddingLeft={8}
                                      paddingRight={8}
                                      paddingY={4}
                                      align="center"
                                      justify="center"
                                      width="fit-content"
                                    >
                                      {statusColorProps.useOutlineDot ? (
                                        <Layout
                                          width={8}
                                          height={8}
                                          borderRadius="circular"
                                          borderColor={statusColorProps.dotColor}
                                          borderWidth={1}
                                          marginRight={8}
                                        />
                                      ) : (
                                        <Circle dimension={8} color={statusColorProps.dotColor} marginRight={8} />
                                      )}
                                      <Text size="tag" color={statusColorProps.textColor} wrap={false} bold>
                                        {statusColorProps.label ?? option.label}
                                      </Text>
                                    </Layout>
                                  </Clickable>
                                )}
                              </>
                            )}
                          </Layout>
                        </Layout>
                      </Layout>
                    );
                  });
                })()
              )}
            </Layout>
            <Layout direction="row" justify="flex-start" paddingX={36}>
              <Clickable onClick={handleClear}>
                <Text size="body-sm" color="gray-700" semibold>
                  Clear selection
                </Text>
              </Clickable>
            </Layout>
          </Layout>
        </Layout>
      )}
    </Layout>
  );
};

export default OneOfOrNotOneOfFilter;
